/**
 * Office-API client (historische naam: Salesforce API Client).
 *
 * Praat uitsluitend met eigen /api-endpoints van de office-app, met het
 * Supabase-token. Sinds 18-09-2026 is de laatste rauwe SF-doorgang
 * (/api/sf-proxy) weg: Salesforce is gecut, de mirror is de waarheid.
 */

import type {
  RouteListItem,
  RouteDetail,
  UnassignedWerkOrder,
  ChauffeurDto,
  MasterRouteDto,
  AddStopRequest,
  ReorderRequest,
  OptimizeRequest,
  ApiResponse,
  WebshopOrder,
  GenerateRoutesRequest,
  GenerateRoutesResult,
  RescheduleRouteResult,
  GeocodeAdresResultaat,
  ExtraVoorstellenResponse,
  ExtraVoorstelBesluitRequest,
  ExtraVoorstelBesluitResult,
} from './types';
import type { MergeRoutesResponse, MergeSnapshot } from './merge-types';
import type { MoveStopsRequest, MoveStopsResponse, MoveSnapshot } from './move-types';
import { supabase } from '../supabase';
import { phoneState } from '../telephony';

class SalesforceClient {
  private async getToken(): Promise<string | null> {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token ?? null;
  }

  /**
   * Unwrap API response from InternalRouteRest format
   */
  private unwrapResponse<T>(response: ApiResponse<T>): T {
    if (!response.success) {
      throw new Error(response.errorMessage || `API Error: ${response.errorCode}`);
    }
    return response.data as T;
  }

  /**
   * Route-verbs via het eigen office-serverpad /api/routes/* (SF-exit kaart
   * 10, fase 2a). De endpoints proxyen byte-compatibel door naar
   * InternalRouteRest, dus de ApiResponse-envelop en unwrapResponse blijven
   * identiek werken. subpath is '' of begint met '/' of '?'.
   */
  private async routesFetch<T>(subpath: string, options: RequestInit = {}): Promise<T> {
    const token = await this.getToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`/api/routes${subpath}`, {
      method: options.method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      ...(options.body !== undefined ? { body: options.body } : {}),
    });

    if (!response.ok) {
      await this.throwFromErrorResponse(response);
    }

    return response.json();
  }

  /**
   * Gedeelde foutafhandeling voor /api/routes-responses.
   * Gooit ALTIJD (return-type never); logt uit bij een verlopen sessie,
   * behalve tijdens een actief telefoongesprek.
   */
  private async throwFromErrorResponse(response: Response): Promise<never> {
    const error = await response.json().catch(() => ({}));

    // Sign out via Supabase if token is invalid
    if (response.status === 401 && (error.error === 'Invalid or expired token' || error.error === 'unauthenticated')) {
      // Harde redirect herlaadt de complete app en trekt daarmee een actief
      // telefoongesprek (WebRTC leeft in deze tab) uit de lucht. Tijdens een
      // levend gesprek dus NIET uitloggen/herladen; de fout bereikt de
      // aanroepende pagina als nette melding en de gebruiker logt ná het
      // gesprek opnieuw in.
      const inCall = phoneState.getCalls().some((c) => c.state !== 'ended');
      if (inCall) {
        throw new Error('Sessie verlopen. Rond je gesprek af en log daarna opnieuw in.');
      }
      await supabase.auth.signOut();
      window.location.href = '/login';
      throw new Error('Session expired. Please log in again.');
    }

    throw new Error(error.message || error.error || error.errorMessage || `API request failed (${response.status}): ${JSON.stringify(error)}`);
  }

  // ==================== Calendar API ====================

  /**
   * Get routes for calendar view within a date range
   * Office-serverpad GET /api/routes (proxy naar InternalRouteRest)
   */
  async getCalendarRoutes(startDate: string, endDate: string, chauffeurId?: string): Promise<RouteListItem[]> {
    let subpath = `?startDate=${startDate}&endDate=${endDate}`;
    if (chauffeurId) {
      subpath += `&chauffeurId=${chauffeurId}`;
    }
    const response = await this.routesFetch<ApiResponse<RouteListItem[]>>(subpath);
    return this.unwrapResponse(response);
  }

  /**
   * Get all chauffeurs (drivers)
   * Office-serverpad GET /api/routes/chauffeurs
   */
  async getChauffeurs(): Promise<ChauffeurDto[]> {
    const response = await this.routesFetch<ApiResponse<ChauffeurDto[]>>('/chauffeurs');
    return this.unwrapResponse(response);
  }

  /**
   * Get all master routes (Route__c records)
   * Office-serverpad GET /api/routes/master-routes
   */
  async getMasterRoutes(): Promise<MasterRouteDto[]> {
    const response = await this.routesFetch<ApiResponse<MasterRouteDto[]>>('/master-routes');
    return this.unwrapResponse(response);
  }

  /**
   * Create a new route
   * Office-serverpad POST /api/routes/create
   */
  async createRoute(data: {
    datum: string;
    routeId?: string;
    chauffeurId?: string;
    capaciteitOphalen?: number;
    capaciteitAfleveren?: number;
  }): Promise<{ id: string; name: string }> {
    const response = await this.routesFetch<ApiResponse<{ id: string; name: string }>>(
      '/create',
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
    return this.unwrapResponse(response);
  }

  /**
   * Extra-rit-voorstellen (plan 2026-10-28). Geen Apex-envelop: het antwoord
   * is het object zelf. `modus=live` voor de kaart: schaduwvoorstellen zijn
   * de ijklijst voor achteraf, geen werk voor kantoor.
   */
  async extraVoorstellen(opts: { status?: string; modus?: 'live' | 'schaduw' | 'alle' } = {}): Promise<ExtraVoorstellenResponse> {
    const qs = new URLSearchParams({ status: opts.status ?? 'concept', modus: opts.modus ?? 'live' });
    return this.routesFetch<ExtraVoorstellenResponse>(`/extra-voorstel?${qs.toString()}`);
  }

  /** Goedkeuren (maakt de rit), overslaan (reden verplicht) of bewerken. */
  async extraVoorstelBesluit(body: ExtraVoorstelBesluitRequest): Promise<ExtraVoorstelBesluitResult> {
    return this.routesFetch<ExtraVoorstelBesluitResult>('/extra-voorstel/besluit', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /**
   * Generate multiple routes for a date range, building on the latest existing week.
   * Office-serverpad POST /api/routes/generate
   */
  async generateRoutes(data: GenerateRoutesRequest): Promise<GenerateRoutesResult> {
    const response = await this.routesFetch<ApiResponse<GenerateRoutesResult>>(
      '/generate',
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
    return this.unwrapResponse(response);
  }

  // ==================== Route API ====================

  /**
   * Get route details with stops
   * Office-serverpad GET /api/routes/{id}
   */
  async getRoute(routeId: string): Promise<RouteDetail> {
    const response = await this.routesFetch<ApiResponse<RouteDetail>>(`/${routeId}`);
    return this.unwrapResponse(response);
  }

  /**
   * Update route (chauffeur assignment, etc.)
   * Office-serverpad PATCH /api/routes/{id}
   */
  async updateRoute(routeId: string, updates: {
    chauffeurId?: string | null;
    locked?: boolean;
    capaciteitOverrideOphalen?: number | null;
    capaciteitAutoHervatOphalen?: boolean;
    capaciteitAutoHervatAfleveren?: boolean;
    capaciteitOverrideAfleveren?: number | null;
    ophalenNietVoorstellen?: boolean;
    afleverenNietVoorstellen?: boolean;
    bijrijderNodig?: boolean;
    bijrijderId?: string | null;
    /** Pauzeknop klantberichten onderweg (plan 2026-10-12). */
    autoUpdatesPauze?: boolean;
    autoUpdatesPauzeReden?: string | null;
  }): Promise<RouteDetail> {
    const response = await this.routesFetch<ApiResponse<RouteDetail>>(`/${routeId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
    return this.unwrapResponse(response);
  }

  /**
   * Lock/unlock a route
   * Office-serverpad POST /api/routes/{id}/lock — bij een geslaagde lock
   * triggert de backend server-side de route-failover-snapshot (Laag 1).
   */
  async setRouteLocked(routeId: string, locked: boolean): Promise<{ success: boolean; locked: boolean }> {
    const response = await this.routesFetch<ApiResponse<{ success: boolean; locked: boolean }>>(`/${routeId}/lock`, {
      method: 'POST',
      body: JSON.stringify({ locked }),
    });
    return this.unwrapResponse(response);
  }

  /**
   * Route annuleren. Office-serverpad POST /api/routes/{id}/cancel (SF-exit
   * kaart 10, fase 2b): WO's terug naar "plannen", stops weg, route
   * Geannuleerd. Klantmail alleen op verzoek (default uit).
   */
  async cancelRoute(
    routeId: string,
    body: { reden: string; toelichting?: string; stuurKlantEmail?: boolean }
  ): Promise<void> {
    const response = await this.routesFetch<ApiResponse<unknown>>(`/${routeId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({
        reden: body.reden,
        toelichting: body.toelichting ?? null,
        stuurKlantEmail: body.stuurKlantEmail === true,
      }),
    });
    this.unwrapResponse(response);
  }

  /**
   * Merge all stops of a source route into a target route.
   * Source route is soft-deleted. Returns a snapshot usable for unmerge.
   * Office-serverpad POST /api/routes/{sourceId}/merge
   */
  async mergeRoutes(
    sourceRouteId: string,
    targetRouteId: string,
    chauffeurId?: string
  ): Promise<MergeRoutesResponse> {
    const response = await this.routesFetch<ApiResponse<MergeRoutesResponse>>(
      `/${sourceRouteId}/merge`,
      {
        method: 'POST',
        body: JSON.stringify({ targetRouteId, chauffeurId: chauffeurId ?? null }),
      }
    );
    return this.unwrapResponse(response);
  }

  /**
   * Undo a previous merge using the snapshot returned by mergeRoutes.
   * Office-serverpad POST /api/routes/{targetId}/unmerge
   */
  async unmergeRoutes(snapshot: MergeSnapshot): Promise<RouteDetail> {
    const response = await this.routesFetch<ApiResponse<RouteDetail>>(
      `/${snapshot.targetRouteId}/unmerge`,
      {
        method: 'POST',
        body: JSON.stringify({ snapshot }),
      }
    );
    return this.unwrapResponse(response);
  }

  /**
   * Move a complete route (with all planned stops) to another day.
   * The chauffeur gets unassigned server-side (route becomes an open shift on
   * the new day) and customers receive the S03B/S08F reschedule mail when
   * notifyCustomers is true. The WO-cascade runs async in Salesforce.
   * Office-serverpad POST /api/routes/{id}/reschedule
   */
  async rescheduleRoute(
    routeId: string,
    newDate: string,
    notifyCustomers: boolean
  ): Promise<RescheduleRouteResult> {
    const { data: { session } } = await supabase.auth.getSession();
    const actorEmail = session?.user?.email ?? undefined;
    const response = await this.routesFetch<ApiResponse<RescheduleRouteResult>>(
      `/${routeId}/reschedule`,
      {
        method: 'POST',
        body: JSON.stringify({ newDate, notifyCustomers, actorEmail }),
      }
    );
    if (!response.success) {
      const err = new Error(response.errorMessage || 'Route verplaatsen mislukt');
      (err as Error & { code?: string }).code = response.errorCode ?? undefined;
      throw err;
    }
    return response.data as RescheduleRouteResult;
  }

  /**
   * Move a subset of stops from a source route to a target route on the same date.
   * Source route stays intact (only loses the selected stops).
   * Office-serverpad POST /api/routes/{sourceId}/stops/move
   */
  async moveStops(sourceRouteId: string, request: MoveStopsRequest): Promise<MoveStopsResponse> {
    const response = await this.routesFetch<ApiResponse<MoveStopsResponse>>(
      `/${sourceRouteId}/stops/move`,
      {
        method: 'POST',
        body: JSON.stringify(request),
      }
    );
    return this.unwrapResponse(response);
  }

  /**
   * Undo a previous stops-move using the snapshot returned by moveStops.
   * Office-serverpad POST /api/routes/{targetId}/stops/unmove
   */
  async unmoveStops(snapshot: MoveSnapshot): Promise<RouteDetail> {
    const response = await this.routesFetch<ApiResponse<RouteDetail>>(
      `/${snapshot.targetRouteId}/stops/unmove`,
      {
        method: 'POST',
        body: JSON.stringify({ snapshot }),
      }
    );
    return this.unwrapResponse(response);
  }

  // ==================== Route Optimization API ====================
  // These map to InternalRouteRest.cls methods

  /**
   * Optimize route via Google Directions API / Route Optimization API
   * Office-serverpad POST /api/routes/{id}/optimize
   *
   * @param routeId - The route to optimize
   * @param options - Optimization options (stoppage times, departure time)
   */
  async optimizeRoute(routeId: string, options?: OptimizeRequest): Promise<RouteDetail> {
    const response = await this.routesFetch<ApiResponse<RouteDetail>>(`/${routeId}/optimize`, {
      method: 'POST',
      body: JSON.stringify(options || {}),
    });
    return this.unwrapResponse(response);
  }

  /**
   * Finalize a route from the office-app — moves all 'Opgehaald' Wasserij_items
   * on this route's completed Ophalen-stops to 'Klaar voor Voorinspectie' and
   * sets Routes__c.Status__c = 'Afgerond'. Tags items with Mutatie_Bron__c='Office-App'.
   * Used when a driver forgot to finalize the route in the field-app.
   * Office-serverpad POST /api/routes/{id}/finalize
   */
  async finalizeRoute(routeId: string): Promise<{
    routeId: string;
    routeName: string;
    itemsUpdated: number;
    finalized: boolean;
    message?: string;
  }> {
    const response = await this.routesFetch<ApiResponse<{
      routeId: string;
      routeName: string;
      itemsUpdated: number;
      finalized: boolean;
      message?: string;
    }>>(
      `/${routeId}/finalize`,
      { method: 'POST', body: JSON.stringify({}) }
    );
    return this.unwrapResponse(response);
  }

  /**
   * Reorder stops after drag & drop and recalculate times
   * Office-serverpad POST /api/routes/{id}/reorder
   *
   * @param routeId - The route to reorder
   * @param request - Stop IDs in new order + optional stoppage time overrides
   */
  async reorderStops(routeId: string, request: ReorderRequest): Promise<RouteDetail> {
    const response = await this.routesFetch<ApiResponse<RouteDetail>>(`/${routeId}/reorder`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
    return this.unwrapResponse(response);
  }

  // ==================== Stop Management API ====================

  /**
   * Add a work order as a stop to a route
   * Office-serverpad POST /api/routes/{id}/stops
   *
   * @param routeId - The route to add the stop to
   * @param request - Work order ID, type, and optional stoppage time
   */
  async addStop(routeId: string, request: AddStopRequest): Promise<RouteDetail> {
    const response = await this.routesFetch<ApiResponse<RouteDetail>>(`/${routeId}/stops`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
    return this.unwrapResponse(response);
  }

  /**
   * Update stop time window (Tijd_vanaf / Tijd_tot)
   * Office-serverpad PATCH /api/routes/{id}/stops/{stopId}
   */
  async updateStopTimeWindow(routeId: string, stopId: string, data: {
    timeFrom?: string | null;
    timeTo?: string | null;
  }): Promise<RouteDetail> {
    const response = await this.routesFetch<ApiResponse<RouteDetail>>(
      `/${routeId}/stops/${stopId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    );
    return this.unwrapResponse(response);
  }

  /**
   * Set/clear the manual "bijrijder nodig" flag on a stop.
   * Same PATCH endpoint as the time-window update; the body carries one extra key.
   * Office-serverpad PATCH /api/routes/{id}/stops/{stopId}
   */
  async setStopBijrijderNodig(routeId: string, stopId: string, value: boolean): Promise<RouteDetail> {
    const response = await this.routesFetch<ApiResponse<RouteDetail>>(
      `/${routeId}/stops/${stopId}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ bijrijderNodigHandmatig: value }),
      }
    );
    return this.unwrapResponse(response);
  }

  /**
   * Re-geocode a stop to fix incorrect coordinates
   * Office-serverpad PATCH /api/routes/{id}/stops/{stopId}
   */
  async regeocodeStop(routeId: string, stopId: string): Promise<RouteDetail> {
    const response = await this.routesFetch<ApiResponse<RouteDetail>>(
      `/${routeId}/stops/${stopId}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ regeocode: true }),
      }
    );
    return this.unwrapResponse(response);
  }

  /**
   * Geocode all stops on a route that are missing coordinates or failed geocoding.
   * Office-serverpad POST /api/routes/{id}/geocode-missing
   */
  async geocodeMissingStops(routeId: string): Promise<RouteDetail> {
    const response = await this.routesFetch<ApiResponse<RouteDetail>>(
      `/${routeId}/geocode-missing`,
      { method: 'POST', body: JSON.stringify({}) }
    );
    return this.unwrapResponse(response);
  }

  /**
   * Zoek een vrij ingetypt adres op (planner-zoekbalk).
   * Office-serverpad GET /api/routes/geocode-adres?adres=
   */
  async geocodeAdres(adres: string): Promise<GeocodeAdresResultaat> {
    const response = await this.routesFetch<ApiResponse<GeocodeAdresResultaat>>(
      `/geocode-adres?adres=${encodeURIComponent(adres)}`
    );
    return this.unwrapResponse(response);
  }

  /**
   * Remove a stop from a route
   * Office-serverpad DELETE /api/routes/{id}/stops/{stopId}
   *
   * @param routeId - The route containing the stop
   * @param stopId - The stop to remove
   * @param newStatus - Optional new status for the werk order (e.g., 'Ophalen plannen', 'Terugbrengen plannen', 'Geannuleerd')
   */
  async removeStop(routeId: string, stopId: string, newStatus?: string): Promise<RouteDetail> {
    let subpath = `/${routeId}/stops/${stopId}`;
    if (newStatus) {
      subpath += `?newStatus=${encodeURIComponent(newStatus)}`;
    }
    const response = await this.routesFetch<ApiResponse<RouteDetail>>(subpath, {
      method: 'DELETE',
    });
    return this.unwrapResponse(response);
  }

  /**
   * Mark one or more stops as "niet gelukt vandaag" (office-initiated).
   * The werk order(s) go back to '...plannen' and the customer(s) get a
   * self-service reschedule mail (NG01/NG02) — free, our fault. The per-stop
   * UI passes a single-element array; the same endpoint serves the bulk action.
   * Office-serverpad POST /api/routes/{id}/niet-gelukt
   *
   * @param routeId - The route containing the stops
   * @param stopIds - The stops (Geplande_Rit__c ids) to mark as not done
   * @param opts - notitie (optional note) and informeerKlant (default true)
   */
  async markStopsNietGelukt(
    routeId: string,
    stopIds: string[],
    opts?: { notitie?: string; informeerKlant?: boolean }
  ): Promise<RouteDetail> {
    const response = await this.routesFetch<ApiResponse<RouteDetail>>(
      `/${routeId}/niet-gelukt`,
      {
        method: 'POST',
        body: JSON.stringify({
          stopIds,
          notitie: opts?.notitie,
          informeerKlant: opts?.informeerKlant ?? true,
        }),
      }
    );
    return this.unwrapResponse(response);
  }

  /**
   * Draai één of meer eerder als "niet gelukt" gemarkeerde (overgeslagen) stops
   * terug naar een GESLAAGDE ophaal/bezorging. De werkorder gaat naar
   * 'Opgehaald'/'Teruggebracht' (klant krijgt de normale ophaal-/bezorgbevestiging),
   * de kleden schuiven door en een eventueel no-show transport-item wordt verwijderd.
   * Spiegel van markStopsNietGelukt — voor de chauffeur-ging-toch-langs-situatie.
   * Office-serverpad POST /api/routes/{id}/toch-geslaagd
   *
   * @param routeId - The route containing the stops
   * @param stopIds - The stops (Geplande_Rit__c ids) to revert to successful
   * @param opts - notitie (optional note for the planning log)
   */
  async markStopsTochGeslaagd(
    routeId: string,
    stopIds: string[],
    opts?: { notitie?: string }
  ): Promise<RouteDetail> {
    const response = await this.routesFetch<ApiResponse<RouteDetail>>(
      `/${routeId}/toch-geslaagd`,
      {
        method: 'POST',
        body: JSON.stringify({
          stopIds,
          notitie: opts?.notitie,
        }),
      }
    );
    return this.unwrapResponse(response);
  }

  // ==================== Unassigned Work Orders API ====================

  /**
   * Get unassigned work orders for the left panel
   * Office-serverpad GET /api/routes/werkorders/unassigned
   *
   * @param type - Filter by type: 'ophalen' or 'afleveren'
   * @param postcode - Filter by postcode prefix (optional)
   */
  async getUnassignedWerkOrders(type?: 'ophalen' | 'afleveren', postcode?: string): Promise<UnassignedWerkOrder[]> {
    let subpath = '/werkorders/unassigned';
    const params = new URLSearchParams();
    if (type) params.append('type', type);
    if (postcode) params.append('postcode', postcode);
    if (params.toString()) subpath += `?${params.toString()}`;

    const response = await this.routesFetch<ApiResponse<UnassignedWerkOrder[]>>(subpath);
    return this.unwrapResponse(response);
  }

  // ==================== Webshop API ====================

  /**
   * Get webshop orders with items (Schoonhuisshop fulfillment)
   *
   * B5 (25-08): leest via GET /api/webshop/orders uit de mirrors — was de
   * grootste levende proxy-SOQL (~20k/week door de 2-minuten-polling). Het
   * endpoint levert exact de oude mapWebshopOrder-vorm.
   */
  async getWebshopOrders(status?: string): Promise<WebshopOrder[]> {
    const token = await this.getToken();
    if (!token) throw new Error('Not authenticated');

    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    const response = await fetch(`/api/webshop/orders${qs}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!response.ok) {
      await this.throwFromErrorResponse(response);
    }
    const body = (await response.json()) as ApiResponse<WebshopOrder[]>;
    return this.unwrapResponse(body);
  }

  // ==================== Customer Match API ====================

  /**
   * Match een klant op email + telefoon via shared resolver-endpoint.
   * Wrapper rond GET /api/customers/match — gebruikt door CustomerSearch om
   * duplicaten te vermijden tijdens nieuwe-klant-invoer (zie customer-matching.md).
   * Returns null op no-match of fout (geen exceptions naar UI).
   */
  async matchCustomer(params: { email?: string; phone?: string }): Promise<{
    id: string; name: string; email: string; phone: string; city: string;
  } | null> {
    const token = await this.getToken();
    if (!token) return null;

    const search = new URLSearchParams();
    if (params.email) search.set('email', params.email);
    if (params.phone) search.set('phone', params.phone);
    if (!search.toString()) return null;

    try {
      const res = await fetch(`/api/customers/match?${search.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return null;
      const data = await res.json() as {
        found: boolean;
        customer: { id: string; name: string; email: string; phone: string; city: string } | null;
      };
      return data.found && data.customer ? data.customer : null;
    } catch {
      return null;
    }
  }

  // ==================== Account Search API ====================

}

export const salesforceClient = new SalesforceClient();
